'use strict';

/**
 * FocusFilters — the shared filter / sort / saved-view engine for the board and
 * the Work Items list. One place to say what a "focus view" is, so both surfaces
 * narrow the same way and a URL means the same thing on either.
 *
 * A filter STATE is a flat, string-only object (so it round-trips through the URL
 * query with no escaping surprises):
 *   {
 *     q:        text search over Title + Description,
 *     status:   comma list of lane keys (Backlog,InProgress,...), '' = all,
 *     type:     comma list of WorkItem types, '' = all,
 *     priority: one Priority, '' = all,
 *     assignee: '' | 'self' | 'human' | 'ai' | 'unassigned' | 'u:<IDUser>',
 *     target:   '' | 'Vision' | 'Module' | 'Feature' | 'none',
 *     recent:   '' | one of RecentWindows.Key (activity window over UpdateDate),
 *     addressed:  '' | 'all' (both) | 'no' (open only) | 'yes' (addressed only) -- Feedback,
 *     customer:   '' (any) | 'none' | '<IDKeystoneCustomer>' -- Feedback's source customer,
 *     sourcetype: '' (any) | one SourceType value -- Feedback's origin,
 *     sort:     '<Field>:<asc|desc>' (list only; the board sorts by lane order),
 *     view:     the Key of the preset / saved view this state came from (a label
 *               hint only; the concrete keys above are what actually filter)
 *   }
 *
 * The functions here are framework-free (no pict, no DOM beyond an optional
 * localStorage for saved views) so a view, the board, or a test can all call them.
 */

// EntityMark is framework-free for the same reason this module is, so searching by a mark costs
// nothing here and reads it with exactly the parser that formed it.
const libEntityMark = require('./EntityMark.js');

// Recent-activity windows, newest-first, measured against a row's UpdateDate
// (falling back to CreateDate). '' / absent means "all time" (no window).
const RecentWindows =
[
	{ Key: '1d', Label: 'Active: last 24 hours', Short: '24h', Ms: 1 * 24 * 60 * 60 * 1000 },
	{ Key: '3d', Label: 'Active: last 3 days',   Short: '3d',  Ms: 3 * 24 * 60 * 60 * 1000 },
	{ Key: '7d', Label: 'Active: last 7 days',   Short: '7d',  Ms: 7 * 24 * 60 * 60 * 1000 },
	{ Key: '2w', Label: 'Active: last 2 weeks',  Short: '2w',  Ms: 14 * 24 * 60 * 60 * 1000 },
	{ Key: '1m', Label: 'Active: last 30 days',  Short: '30d', Ms: 30 * 24 * 60 * 60 * 1000 },
	{ Key: '1y', Label: 'Active: last year',     Short: '1y',  Ms: 365 * 24 * 60 * 60 * 1000 }
];

// The assignee facet choices (coarse role-of-assignee, not one-per-person). Specific
// people are appended by the view from the roster as 'u:<IDUser>'.
const AssigneeChoices =
[
	{ Key: '',           Label: 'Anyone' },
	{ Key: 'self',       Label: 'Me' },
	{ Key: 'human',      Label: 'Any human' },
	{ Key: 'ai',         Label: 'AI agent' },
	{ Key: 'unassigned', Label: 'Unassigned' }
];

// The target facet choices (what a work item is about).
const TargetChoices =
[
	{ Key: '',       Label: 'Any target' },
	{ Key: 'Vision', Label: 'About a Vision' },
	{ Key: 'Module', Label: 'About a Module' },
	{ Key: 'Feature', Label: 'About a Feature' },
	{ Key: 'none',   Label: 'No target' }
];

// The URL keys, in a fixed order so a shared link is stable regardless of the
// order the user touched the controls.
// The workflow sub-state facet (WorkItem): today just the QC kickback marker, so a person can
// pull up everything that did not pass review and is back for rework.
const WorkflowStatusChoices =
[
	{ Key: '',                 Label: 'Any workflow state' },
	{ Key: 'ChangesRequested', Label: 'Changes requested' }
];

// The "activity" lens: a compound focus that always shows the in-flight lanes and, for every
// OTHER lane, only rows touched inside the window. It is not expressible as a plain facet (it
// mixes a status test with a per-status recency test), so filterRows handles the single value
// 'related' specially. Shipped as WorkItem's default, so the list lands on "what is active plus
// what moved lately" (newest first) instead of the whole backlog in database order -- the point
// of #236 for a tenant with tens of thousands of items.
const ActivityActiveStatuses = { InProgress: true, Review: true };
const ActivityWindowMs = 3 * 24 * 60 * 60 * 1000; // resting lanes (Backlog/Todo/Done/Blocked): last 3 days

const StateKeys = ['q', 'status', 'type', 'priority', 'assignee', 'target', 'recent', 'addressed', 'customer', 'sourcetype', 'wfstatus', 'activity', 'sort', 'view'];

// Shipped focus views. Two groups: the five role lenses from the Vision #36
// blueprint, and the concrete named examples from the work item. Each is a
// filter+sort preset; selecting one expands its State onto the live filter.
const Presets =
[
	// The activity lens: in-flight work (InProgress/Review) always, resting lanes only when
	// touched inside the window, newest first. WorkItem's shipped default (see the descriptor),
	// and selectable by hand from the view picker like any other preset.
	{ Key: 'activity',      Group: 'Focus', Label: 'Activity: in-flight + recent', State: { activity: 'related', sort: 'UpdateDate:desc' } },
	// The five role lenses (one board, five ways to read it).
	{ Key: 'role-devops',   Group: 'Roles', Label: 'DevOps: release readiness', State: { status: 'Review,Done,Blocked', sort: 'UpdateDate:desc' } },
	{ Key: 'role-product',  Group: 'Roles', Label: 'Product: vision progress',  State: { target: 'Vision', sort: 'UpdateDate:desc' } },
	{ Key: 'role-project',  Group: 'Roles', Label: 'Project: flow & load',      State: { status: 'InProgress,Review,Blocked', sort: 'UpdateDate:asc' } },
	{ Key: 'role-dev',      Group: 'Roles', Label: 'Developer: my queue',       State: { assignee: 'self', status: 'Todo,InProgress,Review', sort: 'UpdateDate:desc' } },
	{ Key: 'role-ai',       Group: 'Roles', Label: 'Directing AI: agents',      State: { assignee: 'ai', status: 'InProgress,Review', sort: 'UpdateDate:desc' } },
	// The concrete saved-view examples the work item calls out.
	{ Key: 'focus-myopen',  Group: 'Focus', Label: 'My open work',   State: { assignee: 'self', status: 'Backlog,Todo,InProgress,Review', sort: 'WorkItemNumber:asc' } },
	{ Key: 'focus-aiwip',   Group: 'Focus', Label: 'AI in progress', State: { assignee: 'ai', status: 'InProgress', sort: 'UpdateDate:desc' } },
	{ Key: 'focus-review',  Group: 'Focus', Label: 'Needs review',   State: { status: 'Review', sort: 'UpdateDate:asc' } },
	{ Key: 'focus-blocked', Group: 'Focus', Label: 'Blocked',        State: { status: 'Blocked', sort: 'UpdateDate:asc' } }
];

// ── state helpers ────────────────────────────────────────────────────────────

function emptyState()
{
	return { q: '', status: '', type: '', priority: '', assignee: '', target: '', recent: '', addressed: '', customer: '', sourcetype: '', wfstatus: '', activity: '', sort: '', view: '' };
}

// Whether any *filtering* facet is set (sort + view are not filters). Drives the
// "showing N of M" hint and whether the Clear button is meaningful. 'addressed'
// only counts as narrowing when it hides one side ('no'/'yes'); '' and 'all' show both.
function isFiltered(pState)
{
	let tmpState = pState || {};
	return !!(tmpState.q || tmpState.status || tmpState.type || tmpState.priority || tmpState.assignee || tmpState.target || tmpState.recent
		|| (tmpState.addressed && tmpState.addressed !== 'all') || tmpState.customer || tmpState.sourcetype || tmpState.wfstatus || tmpState.activity);
}

// A preset's full state (its partial merged onto an empty state, tagged with its Key).
function presetState(pPreset)
{
	if (!pPreset) { return emptyState(); }
	return Object.assign(emptyState(), pPreset.State || {}, { view: pPreset.Key });
}

function findPreset(pKey)
{
	for (let i = 0; i < Presets.length; i++) { if (Presets[i].Key === pKey) { return Presets[i]; } }
	return null;
}

// ── URL round-trip ───────────────────────────────────────────────────────────

function stateToQuery(pState)
{
	let tmpState = pState || {};
	let tmpParts = [];
	for (let i = 0; i < StateKeys.length; i++)
	{
		let tmpKey = StateKeys[i];
		let tmpValue = tmpState[tmpKey];
		if (tmpValue == null || tmpValue === '') { continue; }
		tmpParts.push(encodeURIComponent(tmpKey) + '=' + encodeURIComponent(String(tmpValue)));
	}
	return tmpParts.join('&');
}

function queryToState(pQuery)
{
	let tmpState = emptyState();
	let tmpQuery = String(pQuery || '');
	if (tmpQuery.charAt(0) === '?') { tmpQuery = tmpQuery.slice(1); }
	if (!tmpQuery) { return tmpState; }
	let tmpPairs = tmpQuery.split('&');
	for (let i = 0; i < tmpPairs.length; i++)
	{
		if (!tmpPairs[i]) { continue; }
		let tmpEq = tmpPairs[i].indexOf('=');
		let tmpKey = decodeURIComponent(tmpEq >= 0 ? tmpPairs[i].slice(0, tmpEq) : tmpPairs[i]);
		let tmpValue = tmpEq >= 0 ? decodeURIComponent(tmpPairs[i].slice(tmpEq + 1)) : '';
		if (StateKeys.indexOf(tmpKey) !== -1) { tmpState[tmpKey] = tmpValue; }
	}
	return tmpState;
}

// The query portion of the current hash (everything after the first '?'), for a
// deep-link / reload read. Safe in a non-browser context (returns '').
function readHashQuery()
{
	if (typeof window === 'undefined' || !window.location) { return ''; }
	let tmpHash = window.location.hash || '';
	let tmpQ = tmpHash.indexOf('?');
	return (tmpQ >= 0) ? tmpHash.slice(tmpQ + 1) : '';
}

// ── an arbitrary view's own query state ──────────────────────────────────────
// The pair above serialises the ENTITY FILTER state, whose keys are a fixed whitelist. A view with
// filters of its own (the issues board's type / customer / assignee / severity) has no home in that
// shape, and squeezing it in would mean widening a whitelist that every entity list shares. These
// two do the same base-preserving replaceState for any flat object, so a view can make its own state
// linkable without owning the URL mechanics.
function writeHashQuery(pParams)
{
	if (typeof window === 'undefined' || !window.location || !window.history
		|| typeof window.history.replaceState !== 'function') { return; }
	let tmpHash = window.location.hash || '';
	let tmpQ = tmpHash.indexOf('?');
	let tmpBase = (tmpQ >= 0) ? tmpHash.slice(0, tmpQ) : tmpHash;
	if (!tmpBase) { tmpBase = '#/'; }
	let tmpParams = pParams || {};
	let tmpParts = Object.keys(tmpParams)
		.filter((pKey) => (tmpParams[pKey] !== null) && (typeof tmpParams[pKey] !== 'undefined') && (tmpParams[pKey] !== ''))
		.map((pKey) => encodeURIComponent(pKey) + '=' + encodeURIComponent(String(tmpParams[pKey])));
	let tmpNewHash = tmpParts.length ? (tmpBase + '?' + tmpParts.join('&')) : tmpBase;
	window.history.replaceState(null, '', (window.location.pathname || '') + (window.location.search || '') + tmpNewHash);
}

function readHashParams()
{
	let tmpOut = {};
	let tmpQuery = readHashQuery();
	if (!tmpQuery) { return tmpOut; }
	tmpQuery.split('&').forEach((pPair) =>
	{
		if (!pPair) { return; }
		let tmpEq = pPair.indexOf('=');
		let tmpKey = (tmpEq >= 0) ? pPair.slice(0, tmpEq) : pPair;
		let tmpValue = (tmpEq >= 0) ? pPair.slice(tmpEq + 1) : '';
		try { tmpOut[decodeURIComponent(tmpKey)] = decodeURIComponent(tmpValue); }
		catch (pError) { /* a malformed pair is not worth losing the rest of the query over */ }
	});
	return tmpOut;
}

// Read the live state from the URL. When the hash carries no focus query (a fresh
// load or a clean deep link), fall back to pDefaults merged onto an empty state --
// this is how Feedback lands on "unaddressed only" the first time, while any URL the
// user has already touched stays authoritative (its explicit values win).
function stateFromHash(pDefaults)
{
	let tmpQuery = readHashQuery();
	if (!tmpQuery) { return Object.assign(emptyState(), pDefaults || {}); }
	return queryToState(tmpQuery);
}

// Rewrite the query on the current hash WITHOUT triggering the router (history
// replace fires no hashchange), so the URL stays shareable and reload-safe while
// the view keeps ownership of rendering. The base path is preserved.
function writeHashState(pState)
{
	if (typeof window === 'undefined' || !window.location || !window.history || typeof window.history.replaceState !== 'function') { return; }
	let tmpHash = window.location.hash || '';
	let tmpQ = tmpHash.indexOf('?');
	let tmpBase = (tmpQ >= 0) ? tmpHash.slice(0, tmpQ) : tmpHash;
	if (!tmpBase) { tmpBase = '#/'; }
	let tmpQuery = stateToQuery(pState);
	let tmpNewHash = tmpQuery ? (tmpBase + '?' + tmpQuery) : tmpBase;
	let tmpURL = (window.location.pathname || '') + (window.location.search || '') + tmpNewHash;
	window.history.replaceState(null, '', tmpURL);
}

// ── assignee classification ──────────────────────────────────────────────────

// Build the context filterRows needs to resolve self / human / AI, from the team
// roster ([{ IDUser, IsAgent }]) and the signed-in user's IDUser.
function buildContext(pRoster, pCurrentUserID)
{
	let tmpAgents = {};
	let tmpRoster = Array.isArray(pRoster) ? pRoster : [];
	for (let i = 0; i < tmpRoster.length; i++)
	{
		if (Number(tmpRoster[i].IsAgent) === 1) { tmpAgents[Number(tmpRoster[i].IDUser)] = true; }
	}
	return { CurrentUserID: Number(pCurrentUserID) || 0, Agents: tmpAgents };
}

function _matchAssignee(pIDAssigned, pFilter, pContext)
{
	let tmpID = Number(pIDAssigned) || 0;
	if (!pFilter) { return true; }
	if (pFilter === 'self') { return tmpID !== 0 && tmpID === pContext.CurrentUserID; }
	if (pFilter === 'unassigned') { return tmpID === 0; }
	if (pFilter === 'ai') { return tmpID !== 0 && !!pContext.Agents[tmpID]; }
	// A human is anyone assigned who is not a known agent (roster gaps read as human).
	if (pFilter === 'human') { return tmpID !== 0 && !pContext.Agents[tmpID]; }
	if (pFilter.indexOf('u:') === 0) { return tmpID === (Number(pFilter.slice(2)) || 0); }
	return true;
}

// ── the filter itself ────────────────────────────────────────────────────────

function _listToSet(pValue)
{
	let tmpValue = String(pValue || '').trim();
	if (!tmpValue) { return null; }
	let tmpSet = {};
	let tmpParts = tmpValue.split(',');
	for (let i = 0; i < tmpParts.length; i++)
	{
		let tmpPart = tmpParts[i].trim();
		if (tmpPart) { tmpSet[tmpPart] = true; }
	}
	return tmpSet;
}

function recentMs(pKey)
{
	for (let i = 0; i < RecentWindows.length; i++) { if (RecentWindows[i].Key === pKey) { return RecentWindows[i].Ms; } }
	return 0;
}

function _parseDate(pValue)
{
	if (!pValue) { return 0; }
	let tmpTime = Date.parse(String(pValue));
	return isNaN(tmpTime) ? 0 : tmpTime;
}

// Loose truthiness for a stored flag. Meadow stores Boolean as SMALLINT, so a row's
// Addressed comes back as 0/1 (number) or '0'/'1' (string) -- and JS makes '0' truthy,
// which this guards against. Missing/null reads as false.
function _truthy(pValue)
{
	if (pValue === null || typeof pValue === 'undefined' || pValue === false) { return false; }
	if (pValue === 0 || pValue === '0' || pValue === '') { return false; }
	return true;
}

/**
 * Filter a flat array of rows by a state. Rows are plain records; the fields read
 * are Status, Type, Priority, TargetType, IDAssignedUser, UpdateDate/CreateDate,
 * Title, Description, and (for Feedback) Addressed, IDKeystoneCustomer, SourceType,
 * Body. Any field a row lacks is simply treated as absent (so the same engine runs
 * over WorkItem rows, Feedback rows, and board card models alike).
 */
// Searching by the mark reads it with the same parser that formed it, so "WI-019" can never mean one
// thing to the search box and another to the thing it is searching.
const splitMark = libEntityMark.splitMark;
const markMatchesQuery = libEntityMark.matchesQuery;

function filterRows(pRows, pState, pContext)
{
	let tmpState = pState || {};
	let tmpContext = pContext || { CurrentUserID: 0, Agents: {} };
	let tmpStatusSet = _listToSet(tmpState.status);
	let tmpTypeSet = _listToSet(tmpState.type);
	let tmpQuery = String(tmpState.q || '').trim().toLowerCase();
	let tmpRecentMs = recentMs(tmpState.recent);
	let tmpAddressed = String(tmpState.addressed || '');
	let tmpCustomer = String(tmpState.customer || '');
	let tmpSourceType = String(tmpState.sourcetype || '');
	let tmpActivity = String(tmpState.activity || '');
	let tmpNow = Date.now();

	return (pRows || []).filter((pRow) =>
	{
		// Activity lens: keep the in-flight lanes always; keep any other lane only when it was
		// touched inside the window. Composes with the explicit facets below (a person can still
		// narrow the activity view by type, assignee, search, etc.).
		if (tmpActivity === 'related')
		{
			let tmpActStatus = String(pRow.Status == null ? '' : pRow.Status);
			if (!ActivityActiveStatuses[tmpActStatus])
			{
				let tmpActWhen = _parseDate(pRow.UpdateDate || pRow.CreateDate);
				if (!tmpActWhen || (tmpNow - tmpActWhen) > ActivityWindowMs) { return false; }
			}
		}
		if (tmpStatusSet && !tmpStatusSet[String(pRow.Status == null ? '' : pRow.Status)]) { return false; }
		if (tmpTypeSet && !tmpTypeSet[String(pRow.Type == null ? '' : pRow.Type)]) { return false; }
		if (tmpState.priority && String(pRow.Priority == null ? '' : pRow.Priority) !== tmpState.priority) { return false; }
		// Addressed lifecycle facet (Feedback): 'no' = open only, 'yes' = addressed only,
		// '' / 'all' = both. A row lacking the column reads as not addressed.
		if (tmpAddressed === 'no' && _truthy(pRow.Addressed)) { return false; }
		if (tmpAddressed === 'yes' && !_truthy(pRow.Addressed)) { return false; }
		// Keystone customer facet (Feedback): a specific IDKeystoneCustomer, or 'none' for
		// feedback tied to no customer. A row lacking the column never matches a specific one.
		if (tmpCustomer)
		{
			let tmpIDCustomer = Number(pRow.IDKeystoneCustomer) || 0;
			if (tmpCustomer === 'none') { if (tmpIDCustomer !== 0) { return false; } }
			else if (String(tmpIDCustomer) !== tmpCustomer) { return false; }
		}
		// Source-type facet (Feedback): exact SourceType match.
		if (tmpSourceType && String(pRow.SourceType == null ? '' : pRow.SourceType) !== tmpSourceType) { return false; }
		// Workflow sub-state facet (WorkItem): exact WorkflowStatus match (e.g. ChangesRequested).
		if (tmpState.wfstatus && String(pRow.WorkflowStatus == null ? '' : pRow.WorkflowStatus) !== tmpState.wfstatus) { return false; }
		if (tmpState.target)
		{
			let tmpTargetType = String(pRow.TargetType == null ? '' : pRow.TargetType);
			if (tmpState.target === 'none') { if (tmpTargetType && tmpTargetType !== 'None') { return false; } }
			else if (tmpTargetType !== tmpState.target) { return false; }
		}
		if (tmpState.assignee && !_matchAssignee(pRow.IDAssignedUser, tmpState.assignee, tmpContext)) { return false; }
		if (tmpRecentMs)
		{
			let tmpWhen = _parseDate(pRow.UpdateDate || pRow.CreateDate);
			if (!tmpWhen || (tmpNow - tmpWhen) > tmpRecentMs) { return false; }
		}
		if (tmpQuery)
		{
			// The searchable text a row can carry: Title + Description (WorkItem),
			// Body (Feedback), Name + Summary (Vision / Module / Feature). A row
			// lacking any of these just contributes '' for it.
			let tmpHay = (String(pRow.Title || '') + ' ' + String(pRow.Name || '') + ' ' + String(pRow.Summary || '') + ' ' + String(pRow.Description || '') + ' ' + String(pRow.Body || '')).toLowerCase();
			// A search that names a mark should find it. People refer to a card as WI-019, or wi-19, or
			// just 19, and none of those appear in the prose. Matched ALONGSIDE the text, never instead
			// of it: "P6" reads as a mark query but is really a phrase in a title, so narrowing to the
			// mark interpretation would lose the result the person was actually looking for.
			if (tmpHay.indexOf(tmpQuery) === -1 && !markMatchesQuery(pRow, tmpQuery)) { return false; }
		}
		return true;
	});
}

// Parse a 'Field:dir' sort string into { Field, Dir }, or null when unset/invalid.
function parseSort(pSort)
{
	let tmpSort = String(pSort || '').trim();
	if (!tmpSort) { return null; }
	let tmpColon = tmpSort.indexOf(':');
	let tmpField = (tmpColon >= 0) ? tmpSort.slice(0, tmpColon) : tmpSort;
	let tmpDir = (tmpColon >= 0) ? tmpSort.slice(tmpColon + 1) : 'asc';
	if (!tmpField) { return null; }
	return { Field: tmpField, Dir: (tmpDir === 'desc') ? 'desc' : 'asc' };
}

// ── saved views (per user, per entity; localStorage) ─────────────────────────
// Personal named presets. The URL is the shareable source of truth; these are the
// user's own quick-switch list, merged with the shipped Presets in the picker.

function _storageKey(pEntity, pUserID)
{
	return 'plansheet.focusviews.' + (pEntity || 'WorkItem') + '.' + (Number(pUserID) || 0);
}

function _localStorage()
{
	try { return (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null; }
	catch (pErr) { return null; }
}

function loadSavedViews(pEntity, pUserID)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return []; }
	try
	{
		let tmpRaw = tmpStore.getItem(_storageKey(pEntity, pUserID));
		let tmpList = tmpRaw ? JSON.parse(tmpRaw) : [];
		return Array.isArray(tmpList) ? tmpList : [];
	}
	catch (pErr) { return []; }
}

function _persistSavedViews(pEntity, pUserID, pList)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return; }
	try { tmpStore.setItem(_storageKey(pEntity, pUserID), JSON.stringify(pList || [])); }
	catch (pErr) { /* storage full / unavailable — saved views are best-effort */ }
}

// Save (or overwrite by name) a named view. Returns the stored view record.
function saveSavedView(pEntity, pUserID, pName, pState)
{
	let tmpName = String(pName || '').trim();
	if (!tmpName) { return null; }
	let tmpList = loadSavedViews(pEntity, pUserID);
	let tmpClean = Object.assign(emptyState(), pState || {});
	tmpClean.view = '';
	let tmpKey = 'saved:' + tmpName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
	let tmpRecord = { Key: tmpKey, Group: 'Saved', Label: tmpName, State: tmpClean };
	let tmpFound = false;
	for (let i = 0; i < tmpList.length; i++)
	{
		if (tmpList[i].Key === tmpKey) { tmpList[i] = tmpRecord; tmpFound = true; break; }
	}
	if (!tmpFound) { tmpList.push(tmpRecord); }
	_persistSavedViews(pEntity, pUserID, tmpList);
	return tmpRecord;
}

function deleteSavedView(pEntity, pUserID, pKey)
{
	let tmpList = loadSavedViews(pEntity, pUserID).filter((pView) => pView.Key !== pKey);
	_persistSavedViews(pEntity, pUserID, tmpList);
	return tmpList;
}

// The full picker list for an entity: shipped Presets first, then the user's saved
// views. A saved view carries its own State; a preset's State is its partial.
function allViews(pEntity, pUserID)
{
	return Presets.concat(loadSavedViews(pEntity, pUserID));
}

function viewState(pEntity, pUserID, pKey)
{
	let tmpAll = allViews(pEntity, pUserID);
	for (let i = 0; i < tmpAll.length; i++)
	{
		if (tmpAll[i].Key === pKey)
		{
			return (tmpAll[i].Group === 'Saved')
				? Object.assign(emptyState(), tmpAll[i].State, { view: pKey })
				: presetState(tmpAll[i]);
		}
	}
	return null;
}

// ── list preferences (per user, per entity; localStorage) ────────────────────
// Which columns a user has shown/hidden on an entity's list, plus the sort they
// last chose there (for entities whose sort does not live in the URL). A flat blob:
//   { columns: { <ColumnField>: <bool> }, sort: '<Field>:<asc|desc>' }
// Absent column keys fall back to the descriptor's own default (DefaultHidden),
// so a column added to a descriptor later still shows up without clearing storage.

function _listPrefsKey(pEntity, pUserID)
{
	return 'plansheet.listprefs.' + (pEntity || '') + '.' + (Number(pUserID) || 0);
}

function loadListPrefs(pEntity, pUserID)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return {}; }
	try
	{
		let tmpRaw = tmpStore.getItem(_listPrefsKey(pEntity, pUserID));
		let tmpObj = tmpRaw ? JSON.parse(tmpRaw) : {};
		return (tmpObj && typeof tmpObj === 'object') ? tmpObj : {};
	}
	catch (pErr) { return {}; }
}

function saveListPrefs(pEntity, pUserID, pPrefs)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return; }
	try { tmpStore.setItem(_listPrefsKey(pEntity, pUserID), JSON.stringify(pPrefs || {})); }
	catch (pErr) { /* storage full / unavailable -- list prefs are best-effort */ }
}

// ── default filter (per user, per entity; localStorage) ──────────────────────
// The user's chosen landing filter for one screen. A full filter STATE (so "none" is a
// real, storable choice -- an empty state -- distinct from "no default set", which is
// absence). When present it overrides the descriptor's shipped Filter.Defaults on a clean
// URL (an explicit URL the user navigated to still wins; see stateFromHash). This is #236's
// "set my own default filter per screen": a person pins the list the way they like it once.

function _defaultStateKey(pEntity, pUserID)
{
	return 'plansheet.focusdefault.' + (pEntity || '') + '.' + (Number(pUserID) || 0);
}

// The stored default state for this entity, or null when the user has set none (fall back to
// the descriptor's shipped default). Round-tripped through the same emptyState shape as the URL.
function loadDefaultState(pEntity, pUserID)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return null; }
	try
	{
		let tmpRaw = tmpStore.getItem(_defaultStateKey(pEntity, pUserID));
		if (!tmpRaw) { return null; }
		let tmpObj = JSON.parse(tmpRaw);
		return (tmpObj && typeof tmpObj === 'object') ? Object.assign(emptyState(), tmpObj) : null;
	}
	catch (pErr) { return null; }
}

// Save the current filter as this user's default for the entity. An empty state is a valid
// default ("none"); to remove the default entirely use clearDefaultState.
function saveDefaultState(pEntity, pUserID, pState)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return; }
	let tmpClean = Object.assign(emptyState(), pState || {});
	try { tmpStore.setItem(_defaultStateKey(pEntity, pUserID), JSON.stringify(tmpClean)); }
	catch (pErr) { /* storage full / unavailable -- default filter is best-effort */ }
}

function clearDefaultState(pEntity, pUserID)
{
	let tmpStore = _localStorage();
	if (!tmpStore) { return; }
	try { tmpStore.removeItem(_defaultStateKey(pEntity, pUserID)); }
	catch (pErr) { /* best-effort */ }
}

module.exports =
{
	writeHashQuery: writeHashQuery,
	readHashParams: readHashParams,
	RecentWindows, AssigneeChoices, TargetChoices, WorkflowStatusChoices, StateKeys, Presets,
	ActivityActiveStatuses, ActivityWindowMs,
	emptyState, isFiltered, presetState, findPreset,
	stateToQuery, queryToState, readHashQuery, stateFromHash, writeHashState,
	buildContext, filterRows, parseSort, recentMs, truthy: _truthy,
	splitMark, markMatchesQuery,
	loadSavedViews, saveSavedView, deleteSavedView, allViews, viewState,
	loadListPrefs, saveListPrefs,
	loadDefaultState, saveDefaultState, clearDefaultState
};
